import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wallet, Plus, ArrowRightLeft, LogOut, Shield, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import BalanceOverview from "@/components/BalanceOverview";
import AccountsSection from "@/components/AccountsSection";
import TransactionsSection from "@/components/TransactionsSection";
import SummarySection from "@/components/SummarySection";
import UsersSection from "@/components/UsersSection";
import NewTransactionDialog from "@/components/NewTransactionDialog";
import TransferDialog from "@/components/TransferDialog";

export default function Dashboard() {
  const { user, isAdmin, logout } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txOpen, setTxOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [accRes, txRes] = await Promise.all([
        api.get("/accounts"),
        api.get("/transactions"),
      ]);
      setAccounts(Array.isArray(accRes.data) ? accRes.data : []);
      setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
    } catch (e) {
      setAccounts([]);
      setTransactions([]);
      toast.error("Veriler yüklenemedi. Backend adresini (config.js) kontrol edin.", {
        duration: 10000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const totals = useMemo(() => {
    const t = { banka: 0, pos: 0, nakit: 0, toplam: 0 };
    (Array.isArray(accounts) ? accounts : []).forEach((a) => {
      t[a.group] += a.balance || 0;
      t.toplam += a.balance || 0;
    });
    return t;
  }, [accounts]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 leading-none">
                Y'ale Pastanesi
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">Mutluluk Paylaştıkça Güzel</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {isAdmin && (
              <>
                <Button
                  data-testid="open-transfer-btn"
                  variant="outline"
                  onClick={() => setTransferOpen(true)}
                  className="gap-2 border-slate-200 text-slate-700"
                >
                  <ArrowRightLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Transfer</span>
                </Button>
                <Button
                  data-testid="open-new-transaction-btn"
                  onClick={() => setTxOpen(true)}
                  className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Yeni İşlem</span>
                </Button>
              </>
            )}
            <div className="hidden md:flex flex-col items-end leading-tight pl-1" data-testid="current-user">
              <span className="text-sm font-medium text-slate-800">
                {user?.name || user?.email}
              </span>
              <Badge
                variant="outline"
                className={`gap-1 mt-0.5 ${
                  isAdmin
                    ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                    : "bg-slate-50 text-slate-600 border-slate-200"
                }`}
              >
                {isAdmin ? <Shield className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {isAdmin ? "Yönetici" : "İzleyici"}
              </Badge>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-slate-500 hover:text-rose-600 hover:bg-rose-50"
              title="Çıkış Yap"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BalanceOverview totals={totals} loading={loading} />

        <Tabs defaultValue="islemler" className="mt-8">
          <TabsList data-testid="dashboard-tabs" className="bg-white border border-slate-200 p-1 rounded-xl">
            <TabsTrigger value="islemler" data-testid="tab-islemler">İşlemler</TabsTrigger>
            <TabsTrigger value="hesaplar" data-testid="tab-hesaplar">Hesaplar</TabsTrigger>
            <TabsTrigger value="ozet" data-testid="tab-ozet">Özet Raporu</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="kullanicilar" data-testid="tab-kullanicilar">Kullanıcılar</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="islemler" className="mt-6">
            <TransactionsSection
              transactions={transactions}
              accounts={accounts}
              onChange={loadAll}
              canEdit={isAdmin}
            />
          </TabsContent>

          <TabsContent value="hesaplar" className="mt-6">
            <AccountsSection accounts={accounts} onChange={loadAll} canEdit={isAdmin} />
          </TabsContent>

          <TabsContent value="ozet" className="mt-6">
            <SummarySection />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="kullanicilar" className="mt-6">
              <UsersSection />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {isAdmin && (
        <>
          <NewTransactionDialog
            open={txOpen}
            onOpenChange={setTxOpen}
            accounts={accounts}
            onSaved={loadAll}
          />
          <TransferDialog
            open={transferOpen}
            onOpenChange={setTransferOpen}
            accounts={accounts}
            onSaved={loadAll}
          />
        </>
      )}
    </div>
  );
}
